import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Bot, Edit, Plus, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentManager = () => {
  const [agents, setAgents] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'dev',
    description: '',
    llm_provider: 'openai',
    llm_model: 'gpt-5',
    system_prompt: '',
    tools: []
  });

  useEffect(() => {
    loadAgents();
    loadAvailableAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadAvailableAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents/available`);
      setAvailableAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error loading available agents:', error);
    }
  };

  const createAgent = async () => {
    try {
      await axios.post(`${API}/agents`, formData);
      toast.success('Агент создан');
      setOpen(false);
      loadAgents();
      resetForm();
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error('Ошибка создания агента');
    }
  };

  const updateAgent = async () => {
    try {
      await axios.put(`${API}/agents/${editingAgent.id}`, formData);
      toast.success('Агент обновлён');
      setOpen(false);
      setEditingAgent(null);
      loadAgents();
      resetForm();
    } catch (error) {
      console.error('Error updating agent:', error);
      toast.error('Ошибка обновления агента');
    }
  };

  const deleteAgent = async (id) => {
    try {
      await axios.delete(`${API}/agents/${id}`);
      toast.success('Агент удалён');
      loadAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Ошибка удаления агента');
    }
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      type: agent.type,
      description: agent.description,
      llm_provider: agent.llm_provider,
      llm_model: agent.llm_model,
      system_prompt: agent.system_prompt,
      tools: agent.tools || []
    });
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'dev',
      description: '',
      llm_provider: 'openai',
      llm_model: 'gpt-5',
      system_prompt: '',
      tools: []
    });
  };

  return (
    <div className="h-full overflow-auto p-8" data-testid="agent-manager">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Управление Агентами</h1>
          <p className="text-gray-400">Создавайте и настраивайте AI агентов</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600"
              onClick={resetForm}
            >
              <Plus className="w-4 h-4 mr-2" />
              Создать агента
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 border-white/20 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingAgent ? 'Редактировать агента' : 'Новый агент'}</DialogTitle>
              <DialogDescription className="text-gray-400">
                Настройте параметры агента
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label>Название</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>
              <div>
                <Label>Тип агента</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/20">
                    <SelectItem value="dev" className="text-white">DevAgent</SelectItem>
                    <SelectItem value="ssh" className="text-white">SSHAgent</SelectItem>
                    <SelectItem value="fastpanel" className="text-white">FastPanelAgent</SelectItem>
                    <SelectItem value="testing" className="text-white">TestingAgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Описание</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>LLM Provider</Label>
                  <Select value={formData.llm_provider} onValueChange={(value) => setFormData({...formData, llm_provider: value})}>
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/20">
                      <SelectItem value="openai" className="text-white">OpenAI</SelectItem>
                      <SelectItem value="anthropic" className="text-white">Anthropic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Модель</Label>
                  <Select value={formData.llm_model} onValueChange={(value) => setFormData({...formData, llm_model: value})}>
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue placeholder="Выберите модель" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/20">
                      <SelectItem value="gpt-5" className="text-white">GPT-5 (Latest)</SelectItem>
                      <SelectItem value="gpt-4o" className="text-white">GPT-4o</SelectItem>
                      <SelectItem value="gpt-4" className="text-white">GPT-4</SelectItem>
                      <SelectItem value="claude-4-5-sonnet-20250219" className="text-white">Claude 4.5 Sonnet (Latest)</SelectItem>
                      <SelectItem value="claude-3-7-sonnet-20250219" className="text-white">Claude 3.7 Sonnet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>System Prompt</Label>
                <Textarea
                  value={formData.system_prompt}
                  onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                  className="bg-white/5 border-white/20 text-white font-mono text-sm"
                  rows={8}
                  placeholder="Вы - эксперт по..."
                />
              </div>
              <Button
                onClick={editingAgent ? updateAgent : createAgent}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600"
              >
                {editingAgent ? 'Обновить' : 'Создать'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Available Agents Info */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-4">Доступные типы агентов</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableAgents.map((agent, index) => (
            <Card key={index} className="glass-panel border-white/10 bg-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-violet-400" />
                  {agent.name}
                </CardTitle>
                <CardDescription className="text-gray-400 text-sm">
                  {agent.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-300">Возможности:</p>
                  <div className="flex flex-wrap gap-2">
                    {agent.capabilities.map((cap, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 rounded-md text-xs bg-violet-500/20 text-violet-300 border border-violet-500/30"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Custom Agents */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-4">Ваши агенты</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => (
            <Card key={agent.id} className="glass-panel border-white/10 bg-white/5">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Bot className="w-5 h-5 text-blue-400" />
                      {agent.name}
                    </CardTitle>
                    <CardDescription className="text-gray-400 text-sm mt-1">
                      {agent.type} • {agent.llm_provider}/{agent.llm_model}
                    </CardDescription>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${agent.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300 text-sm mb-4">{agent.description}</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditDialog(agent)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Изменить
                  </Button>
                  <Button
                    onClick={() => deleteAgent(agent.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentManager;
